import { Box, Stack, Typography } from "@compass/core";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SyncIcon from "@mui/icons-material/Sync";
import CircularProgress from "@mui/material/CircularProgress";
import * as signalR from "@microsoft/signalr";

// Define TypeScript interface for the component props
interface ConnectionStatusProps {
  status: signalR.HubConnectionState;
}

// Define the config type for better type safety
interface StatusConfig {
  icon: React.ReactNode;
  label: string;
  color: "success" | "warning" | "error" | "info";
}

// Add this function to your component
export const ConnectionStatus: React.FC<ConnectionStatusProps> = ({ status }) => {
  // Configuration for each status
  // @ts-ignore
  const statusConfig: Record<signalR.HubConnectionState, StatusConfig> = {
    [signalR.HubConnectionState.Connected]: {
      icon: <CheckCircleIcon fontSize="small" />,
      label: "Connected",
      color: "success",
    },
    [signalR.HubConnectionState.Reconnecting]: {
      icon: (
        <SyncIcon
          fontSize="small"
          sx={{
            animation: "spin 1.5s linear infinite",
            "@keyframes spin": {
              "0%": {
                transform: "rotate(0deg)",
              },
              "100%": {
                transform: "rotate(360deg)",
              },
            },
          }}
        />
      ),
      label: "Reconnecting...",
      color: "warning",
    },
    [signalR.HubConnectionState.Disconnected]: {
      icon: <ErrorIcon fontSize="small" />,
      label: "Disconnected",
      color: "error",
    },
    [signalR.HubConnectionState.Connecting]: {
      icon: <CircularProgress size={16} color="info" />,
      label: "Connecting...",
      color: "info",
    },
  };

  // Get config for current status or fallback to Connecting if somehow invalid
  const config = statusConfig[status] || statusConfig[signalR.HubConnectionState.Connecting];

  return (
    <Box
      sx={{
        borderRadius: 1,
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <Stack direction="row" spacing={"xs"} alignItems="center" justifyContent={"center"}>
        <Box
          sx={{
            color: `${config.color}.main`,
            display: "flex",
            alignItems: "center",
          }}
        >
          {config.icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: `${config.color}.main`,
            display: "flex",
            alignItems: "center",
            lineHeight: 1,
          }}
        >
          {config.label}
        </Typography>
      </Stack>
    </Box>
  );
};
